/**
 * 設定情報シートの印刷画面（`/print/mail-setup?ids=...`）。
 *
 * ## なぜ独立した画面なのか
 *
 * この紙は**アプリの外へ出ていく成果物**（新入社員に配る / 情シスが台帳に
 * 綴じる）で、画面の一部ではない。サイドナビやパンくずが付いたレイアウトの
 * 中に置くと、`@media print` でそれらを消す指定を全画面ぶん書くことになり、
 * 1 か所直すたびに他の画面の印刷が壊れる。**レイアウトの外**に出して、
 * この画面だけが紙のことを知っている形にする。
 *
 * ## 認証は要る
 *
 * 接続先とアドレスは契約者の情報なので、ログインしていない人には見せない。
 * ルート木では `AppLayout` を持たない認証ガード（`printRoute`）の下に置く。
 *
 * ## PDF 化
 *
 * ブラウザの印刷ダイアログで「PDF として保存」を選べば PDF になる。
 * そのためにアプリ側で PDF を作る必要はない（フォント埋め込みも要らない）。
 * 複数アドレスを 1 回で出すために、`ids` はカンマ区切りで複数受ける。
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle, Button } from '@mail-sv/ui';
import { ArrowLeft, Printer } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink, useSearchParams } from '../lib/nav.js';
import { Async } from '../components/async.js';
import { SetupSheetCard, SetupSheetStyles } from '../components/setup-sheet.js';
import { env } from '../env.js';
import type { SetupSheet } from '../lib/api-types.js';

interface SetupSheetsResponse {
  items: SetupSheet[];
  /** 要求した件数。返ってきた件数と違うときだけ画面で断りを入れる。 */
  requested: number;
}

/** `?ids=a,b,c` を配列にする。空要素と重複は落とす。 */
export function parseIds(raw: unknown): string[] {
  if (typeof raw !== 'string') return [];
  return [...new Set(raw.split(',').map((s) => s.trim()).filter((s) => s !== ''))];
}

export function PrintSetupSheetPage(): React.JSX.Element {
  const search = useSearchParams();
  const ids = React.useMemo(() => parseIds(search.ids), [search.ids]);

  const sheets = useQuery({
    queryKey: qk.setupSheets(ids),
    queryFn: () =>
      api.get<SetupSheetsResponse>(
        `/mailboxes/setup-sheets?ids=${encodeURIComponent(ids.join(','))}`,
      ),
    enabled: ids.length > 0,
    // 紙は「いま画面に出ている値」を刷るもの。開き直したら取り直す。
    staleTime: 0,
  });

  /*
   * 接続情報を紙に載せるか。既定は載せる（本人へ渡す控えが主な用途）。
   * 画面の選択なので URL には持たせない（紙の内容が履歴に残る意味が無い）。
   */
  const [includeSettings, setIncludeSettings] = React.useState(true);

  return (
    <div className="min-h-screen bg-background p-4 print:bg-white print:p-0">
      <SetupSheetStyles />

      {/* 画面にだけ出る操作列。`sheet-noprint` で紙からは消える。 */}
      <div className="sheet-noprint mx-auto mb-6 flex max-w-[190mm] flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <AppLink to="/mailboxes">
            <ArrowLeft aria-hidden />
            メールアドレス一覧へ戻る
          </AppLink>
        </Button>
        <div className="flex flex-wrap items-center gap-4">
          {/*
            **接続情報を紙に載せるかを選べるようにする。**
            本人へ渡す控えなら要るが、席に置く / 名簿に綴じるといった
            「アドレスだけ分かればよい」使い方では要らない。
            **紙は回収も失効もできない**ので、要らない情報は載せないほうがよい。
          */}
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4"
              checked={includeSettings}
              onChange={(e) => setIncludeSettings(e.target.checked)}
            />
            メールソフトに入力する値も印刷する
          </label>
          <Button onClick={() => window.print()} disabled={!sheets.data?.items.length}>
            <Printer aria-hidden />
            印刷する（PDF 保存もできます）
          </Button>
        </div>
      </div>

      {ids.length === 0 ? (
        <div className="sheet-noprint mx-auto max-w-[190mm]">
          <Alert variant="warning">
            <AlertTitle>印刷するメールアドレスが選ばれていません</AlertTitle>
            <AlertDescription>
              メールアドレスの一覧または詳細から「設定情報を印刷」を押してください。
            </AlertDescription>
          </Alert>
        </div>
      ) : (
        <Async query={sheets} lines={6}>
          {(data) => (
            <>
              {/*
                選んだのに出てこない件（他のメンバーのメールアドレスを選んだ、
                その間に削除された、など）は**黙って落とさない**。
                束で渡す紙なので、枚数が合わないことに気づけないと事故になる。
              */}
              {data.items.length < data.requested ? (
                <div className="sheet-noprint mx-auto mb-4 max-w-[190mm]">
                  <Alert variant="warning">
                    <AlertTitle>
                      {data.requested} 件のうち {data.items.length} 件だけを表示しています
                    </AlertTitle>
                    <AlertDescription>
                      削除されたか、お客様の権限では扱えないメールアドレスが含まれていました。
                    </AlertDescription>
                  </Alert>
                </div>
              ) : null}

              {data.items.map((sheet) => (
                <SetupSheetCard
                  key={sheet.mailboxId}
                  sheet={sheet}
                  brandName={env.brandName}
                  includeSettings={includeSettings}
                />
              ))}
            </>
          )}
        </Async>
      )}
    </div>
  );
}
